import React, {useCallback, useState, useEffect} from 'react';
import useIsMobile from '../../../hooks/useIsMobile';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import {Button, Divider, Typography, message} from 'antd';
import { CaretDownOutlined, CaretUpOutlined, SoundFilled } from '@ant-design/icons';
import useAudioCache from '../../../hooks/useAudioCache';
import useTranslation from '../../../hooks/useTranslation';
import { randomInt, shuffled } from '../../../utils/random';

const {Text} = Typography;


/** Нажатая плитка розовеет при ошибке; верная и все прочие остаются белыми. */
const tileBackground = (buttonState, index) =>
    buttonState.index === index && !buttonState.isCorrect ? '#ffdada' : 'white';

function SentenceBuildingFromWordEnglishGbWordCard({
                                                       word,
                                                       handleCorrectClick,
                                                       handleIncorrectClick,
                                                       locale,
                                                   }) {
    const isMobile = useIsMobile();
    const { t } = useTranslation();
    const { playAudio } = useAudioCache(
        `${word.headword}|${word.partOfSpeech}|${locale.code}`, locale.code);
    const [openExamples, setOpenExamples] = useState({});
    const [selectedWords, setselectedWords] = useState([]);
    const [shuffledWords, setShuffledWords] = useState([]);
    const [errors, setErrors] = useState(0);
    const [buttonState, setButtonState] = useState({index: null, isCorrect: null});
    const [isSentensComplete, setIsSentensComplete] = useState(false);
    const [isCorrectButtonDisabled, setIsCorrectButtonDisabled] = useState(false);
    const [currentDefinitionIndex, setCurrentDefinitionIndex] = useState(0);
    // const wordsArray = chosenExample.sentence.split(' ');


    const [currentExample, setCurrentExample] = useState({sentence: '', translation: '', id: null});


    const shuffleWords = (sentence) =>
        // Объекты с id, словом и флагом chosen
        shuffled(sentence.split(' ')).map((word, index) => ({id: index, word, chosen: false}));

    const initializeComponentState = useCallback(() => {
        if (word?.wordDefinition?.[currentDefinitionIndex]?.examples?.length > 0) {
            const examples = word.wordDefinition[currentDefinitionIndex].examples;
            const randomIndex = randomInt(examples.length);
            setCurrentExample(examples[randomIndex]);

            const wordsToShuffle = examples[randomIndex].sentence;
            setShuffledWords(shuffleWords(wordsToShuffle));
        } else {
            setCurrentExample(null);
            setShuffledWords([]);
        }

        setOpenExamples({});
        setselectedWords([]);
        setIsSentensComplete(false);
        setIsCorrectButtonDisabled(false);
        setErrors(0);
    }, [word, currentDefinitionIndex]);

    useEffect(() => {
        if (word?.wordDefinition?.[currentDefinitionIndex]?.examples?.length > 0) {
            const examples = word.wordDefinition[currentDefinitionIndex].examples;
            const randomIndex = randomInt(examples.length);
            setCurrentExample(examples[randomIndex]);
        } else {
            setCurrentExample(null);
        }
    }, [word, currentDefinitionIndex]);


    useEffect(() => {
        if (word.wordDefinition?.length > 0 && currentExample) {
            const wordsToShuffle = currentExample.sentence;
            setShuffledWords(shuffleWords(wordsToShuffle));
            setOpenExamples({});
            setselectedWords([]);
            setIsSentensComplete(false);
            setIsCorrectButtonDisabled(false);
            setErrors(0);
        }
    }, [word, currentExample]);

    useEffect(() => {
        initializeComponentState();
    }, [word, initializeComponentState]);



    const toggleExamples = (index) => {
        setOpenExamples((prevState) => ({
            ...prevState,
            [index]: !prevState[index],
        }));
    };

    // Озвучка слова проигрывается при показе карточки — поведение практики
    // сохранено, теперь по ref из ответа и через общий кэш useAudioCache.
    useEffect(() => {
        if (word.audio?.access === 'ALLOWED') {
            playAudio(word.audio.ref);
        }
    }, [word, playAudio]);



    // Замок в данных: PLAN_REQUIRED объясняется словами, а не тишиной.
    const playExampleAudio = (example) => {
        if (example.audio?.access === 'ALLOWED' || example.audio?.access === 'NOT_READY') {
            playAudio(example.audio.ref, example.audio.access);
        } else if (example.audio?.access === 'PLAN_REQUIRED') {
            message.info(t('common.exampleAudioPremium'));
        }
    };

    const handleNextClick = () => {
        // Проверяем, завершено ли предложение
        if (isSentensComplete) {
            // Проверяем, есть ли следующий элемент в wordDefinition
            if (currentDefinitionIndex < word.wordDefinition.length - 1) {
                // Переходим к следующему определению
                setCurrentDefinitionIndex((prevIndex) => prevIndex + 1);
            } else {
                // Если определений больше нет, выполняем текущую логику отправки результатов
                initializeComponentState();
                if (errors === 0) {
                    handleCorrectClick({ attempts: errors + 1 });
                } else {
                    handleIncorrectClick({ attempts: errors + 1 });
                }
            }
        }
    };


    const handleWordClick = (clickedWord, index) => {
        setIsCorrectButtonDisabled(true);

        const formedSentence = [...selectedWords, clickedWord].join(' ');
        if (!currentExample) return;

        const isCorrect = currentExample.sentence.startsWith(formedSentence);

        console.log('currentExample.sentence:', currentExample.sentence);
        console.log('formedSentence:', formedSentence);

        setButtonState({index, isCorrect});

        if (isCorrect) {
            const newSelectedWords = [...selectedWords, clickedWord];
            setselectedWords(newSelectedWords);

            // Отмечаем выбранное слово как chosen
            setShuffledWords((prevShuffledWords) =>
                prevShuffledWords.map((item, i) =>
                    i === index ? {...item, chosen: true} : item
                )
            );

            // Проверяем, завершено ли предложение после объявления newSelectedWords
            if (newSelectedWords.join(' ') === currentExample.sentence) {
                setIsSentensComplete(true);
                playExampleAudio(currentExample);
            }

        } else {
            setErrors((prevErrors) => prevErrors + 1);
        }

        setTimeout(() => setButtonState({index: null, isCorrect: null}), 500);
    };




    return (
        <div
            style={{
                padding: isMobile ? '0' : '0px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: 'calc(100vh - 80px)',
                overflow: 'hidden',
                width: isMobile ? '100%' : '33%',
                margin: isMobile ? '0' : 'auto',
            }}
        >
            <Card style={{
                flex: '1 1 auto',
                display: 'flex',
                flexDirection: 'column',
                padding: '0px'
            }}>
                <CardContent style={{flexGrow: 1, padding: isMobile ? '0px' : '20px'}}>

                    <div
                        style={{
                            visibility: isSentensComplete ? 'visible' : 'hidden', // Контроль видимости
                            height: 'auto', // Автоматическая высота для видимого содержимого
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            marginBottom: '0px'
                        }}
                    >
                        <Text strong style={{fontSize: '2em', color: '#838383', textAlign: 'center'}}>
                            {word.headword}
                        </Text>
                        <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                            <button
                                type="button"
                                aria-label={t('common.listen')}
                                style={{
                                    borderRadius: '25%',
                                    padding: '3px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    background: 'none',
                                    border: '1.5px solid #838383',
                                }}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    playAudio(word.audio?.ref, word.audio?.access);
                                }}
                            >
                                <SoundFilled style={{fontSize: '0.9em', color: '#838383'}}/>
                            </button>
                            <Text
                                style={{fontSize: '1em', marginLeft: '8px', color: '#838383'}}>/{word.phonetic}/</Text>
                        </div>
                        <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                            <Text strong
                                  style={{fontSize: '1.2em', marginTop: '8px', textAlign: 'center', color: '#838383'}}>
                                {word.partOfSpeech}
                            </Text>
                        </div>
                    </div>


                    <Divider style={{margin: isMobile ? '0px 0' : '10px 0'}}/>


                    <br/>
                    <br/>


                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginTop: '10px',
                            minHeight: '32px',
                            marginLeft: '15px',
                            marginRight: '15px',
                        }}
                    >

                        <button
                            type="button"
                            aria-label={t('common.listen')}
                            style={{
                                border: '1px solid #027b83',
                                borderRadius: '25%',
                                padding: '3px',
                                display: 'inline-flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                cursor: 'pointer',
                                background: 'none',
                                marginRight: '8px',
                                visibility: isSentensComplete ? 'visible' : 'hidden', // вместо условного рендеринга
                            }}
                            onClick={(event) => {
                                event.stopPropagation();
                                playExampleAudio(currentExample);
                            }}
                        >
                            <SoundFilled style={{fontSize: '0.9em', color: '#027b83'}}/>
                        </button>


                        <Text strong style={{fontSize: '1.2em', color: '#027b83'}}>
                            {selectedWords.join(' ')}
                        </Text>
                    </div>


                    <br/>


                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginTop: '10px',
                        marginLeft: '15px',
                        marginRight: '15px',

                    }}>
                        <Text type="secondary" italic style={{fontSize: '1em'}}>
                            {currentExample?.translation || ''}
                        </Text>
                    </div>


                    {/*Отображение данных*/}


                    <Divider style={{margin: '30px 0'}}/>


                    {isSentensComplete && (
                        <div style={{marginTop: '0px', marginRight: '10px'}}>
                            {word.wordDefinition.map((def, index) => (
                                index === currentDefinitionIndex && (
                                    <div key={def.id} style={{marginBottom: '16px'}}>
                                        <button
                                            type="button"
                                            aria-expanded={Boolean(openExamples[index])}
                                            onClick={() => toggleExamples(index)}
                                            style={{
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                background: 'none', border: 'none', padding: 0, font: 'inherit', textAlign: 'left', width: '100%'
                                            }}
                                        >
                                            {openExamples[index] ?
                                                <CaretUpOutlined style={{marginLeft: '8px', color: '#d9d9d9'}}/> :
                                                <CaretDownOutlined style={{marginLeft: '8px', color: '#d9d9d9'}}/>}

                                            <Text strong style={{marginLeft: '8px'}}>{def.definition}</Text>
                                        </button>
                                        <div style={{marginTop: '4px'}}>
                                            <Text italic strong style={{marginLeft: '36px', color: '#ee6002'}}>
                                                {def.translation}
                                            </Text>
                                        </div>

                                        {openExamples[index] && (
                                            <div style={{marginTop: '10px', marginLeft: '16px'}}>
                                                {def.examples.map((example, exampleIndex) => (
                                                    <div key={example.id} style={{marginBottom: '8px'}}>
                                                        <button
                                                            type="button"
                                                            aria-label={t('common.listen')}
                                                            style={{
                                                                border: '1px solid #027b83',
                                                                borderRadius: '25%',
                                                                padding: '3px',
                                                                display: 'inline-flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                cursor: 'pointer',
                                                                background: 'none',
                                                                marginRight: '8px',
                                                            }}
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                playExampleAudio(example);
                                                            }}
                                                        >
                                                            <SoundFilled style={{fontSize: '0.6em', color: '#027b83'}}/>
                                                        </button>
                                                        <Text>{example.sentence}</Text>
                                                        <br/>
                                                        <Text type="secondary">{example.translation}</Text>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )
                            ))}
                        </div>
                    )}


                    {/*Конец блока Отображение данных*/}



                    {!isSentensComplete && (
                    <div style={{
                        justifyContent: 'center', // Центрирование по горизонтали
                        alignItems: 'center', // Центрирование по вертикали внутри контейнера
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '10px',

                        marginTop: '25px',

                        //     marginTop: '10%', // Отступ сверху на 60% высоты экрана
                        // transform: 'translateY(-50%)', // Центрирование блока на середине экрана с учётом отступа


                    }}>


                        {shuffledWords.map(({id, word, chosen}, index) => (
                            <Button
                                key={id}
                                onClick={() => !chosen && handleWordClick(word, index)}
                                type="text"
                                disabled={chosen}
                                style={{
                                    fontSize: '1.5em',
                                    fontWeight: 'bold',
                                    padding: '10px 15px',
                                    color: '#027b83',
                                    borderColor: '#027b83',
                                    backgroundColor: tileBackground(buttonState, index),
                                    transition: 'background-color 0.3s ease, opacity 0.3s ease',
                                    pointerEvents: chosen ? 'none' : 'auto',
                                    visibility: chosen ? 'hidden' : 'visible',
                                }}
                            >
                                {word}
                            </Button>
                        ))}


                    </div>
                        )}

                </CardContent>

                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        paddingTop: '6px',
                        paddingLeft: '20px',
                        paddingBottom: '20px',
                        paddingRight: '20px',
                    }}
                >
                    <Button
                        onClick={handleCorrectClick}
                        disabled={isCorrectButtonDisabled} // Кнопка неактивна, если хотя бы одна буква выбрана
                        style={{
                            width: isMobile ? '100%' : '150px',
                            marginRight: '10px',
                            cursor: isCorrectButtonDisabled ? 'default' : 'pointer', // Курсор меняется в зависимости от состояния
                        }}
                    >
                        Know
                    </Button>

                    <Button
                        onClick={handleNextClick}
                        disabled={!isSentensComplete} // Отключена, если слово не завершено
                        style={{
                            width: isMobile ? '100%' : '150px',
                            cursor: isSentensComplete ? 'pointer' : 'default', // Меняем курсор
                        }}
                    >
                        Next
                    </Button>


                </div>
            </Card>
        </div>
    );
}

export default SentenceBuildingFromWordEnglishGbWordCard;
